import React, { useMemo, useState } from 'react';

import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { Alert, Button, CircularProgress, Stack, TableContainer, Typography } from '@mui/material';

import type { OpportunityRelatedDeal, OpportunityRelatedInclusion } from '~/hooks/apis/offers/useGetOpportunityRelated';

import type { SalesforceSurchargePeriod } from '~/types/surcharges';

import getErrorMessage from '~/utils/getErrorMessage';

import { useDealInformationTabCount } from '../../DealInformationTabCounts';
import TabPanelShell from '../../shared/TabPanelShell';
import type { SurchargeRoomAssignment } from '../../types';

import { type DealOptionDetailTab } from './DealOptionDetails';
import DealOptionRoomInfoDialog from './DealOptionRoomInfoDialog';
import DealOptionsTable from './DealOptionsTable';
import { type DealOptionGroup, buildDealOptionsViewModel } from './dealOptionsModel';
import {
  type RoomInfoTab,
  type SelectedRoomInfo,
  getGroupRoomRateIds,
  getScopedBlackoutEntries,
  getScopedEgsRows,
  getScopedSurcharges,
} from './dealOptionsRoomInfo';

interface Props {
  appliedRoomsBySurchargeId: Record<string, Array<SurchargeRoomAssignment>>;
  blackoutDatesByRoomRate: Record<string, Array<App.BlackoutDate>>;
  deals: Array<OpportunityRelatedDeal>;
  extraGuestSurchargesByRoomRate: Record<string, Array<App.ExtraGuestSurcharges>>;
  error?: unknown;
  inclusions: Array<OpportunityRelatedInclusion>;
  isLoading: boolean;
  packages: Array<App.Package>;
  salesforceSurcharges: Array<SalesforceSurchargePeriod>;
}

export default function DealOptionsTab({
  appliedRoomsBySurchargeId,
  blackoutDatesByRoomRate,
  deals,
  error,
  extraGuestSurchargesByRoomRate,
  inclusions,
  isLoading,
  packages,
  salesforceSurcharges,
}: Props) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [activeDetailTabs, setActiveDetailTabs] = useState<Record<string, DealOptionDetailTab>>({});
  const [roomInfoTab, setRoomInfoTab] = useState<RoomInfoTab>('surcharges');
  const [selectedRoomInfo, setSelectedRoomInfo] = useState<SelectedRoomInfo | null>(null);
  const viewModel = useMemo(
    () => buildDealOptionsViewModel({ deals, inclusions, packages }),
    [deals, inclusions, packages],
  );
  const allGroupIds = useMemo(() => viewModel.groups.map((group) => group.id), [viewModel.groups]);
  const roomCount = viewModel.groups.length;

  useDealInformationTabCount('deal-options', isLoading || error ? undefined : deals.length);

  const toggleAllGroups = () => {
    setExpandedGroups((currentGroups) =>
      currentGroups.size === allGroupIds.length ? new Set() : new Set(allGroupIds),
    );
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((currentGroups) => {
      const nextGroups = new Set(currentGroups);
      if (nextGroups.has(groupId)) {
        nextGroups.delete(groupId);
      } else {
        nextGroups.add(groupId);
      }
      return nextGroups;
    });
  };

  const toggleRow = (rowId: string) => {
    setExpandedRows((currentRows) => {
      const nextRows = new Set(currentRows);
      if (nextRows.has(rowId)) {
        nextRows.delete(rowId);
      } else {
        nextRows.add(rowId);
      }
      return nextRows;
    });
  };

  const getRoomInfo = (group: DealOptionGroup) => {
    const roomRateIds = getGroupRoomRateIds(group);
    const scopedBlackouts = getScopedBlackoutEntries({
      blackoutsByRoomRateId: blackoutDatesByRoomRate,
      roomRateIds,
    });
    const scopedEgsRows = getScopedEgsRows({
      egsByRoomRateId: extraGuestSurchargesByRoomRate,
      roomRateIds,
    });
    const scopedSurcharges = getScopedSurcharges({
      appliedRoomsBySurchargeId,
      roomRateIds,
      surcharges: salesforceSurcharges,
    });

    return {
      blackouts: scopedBlackouts,
      egsRows: scopedEgsRows,
      surcharges: scopedSurcharges,
    };
  };

  const openRoomInfo = (group: DealOptionGroup, roomInfo: Omit<SelectedRoomInfo, 'group'>) => {
    setRoomInfoTab('egs');
    setSelectedRoomInfo({ ...roomInfo, group });
  };

  const handleActiveDetailTabChange = (rowId: string, tab: DealOptionDetailTab) => {
    setActiveDetailTabs((currentTabs) => ({ ...currentTabs, [rowId]: tab }));
  };

  if (isLoading) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading deal options.</Typography>
      </Stack>
    );
  }

  if (error) {
    return <Alert severity="error">{getErrorMessage(error, 'Unable to load Salesforce deal options.')}</Alert>;
  }

  return (
    <TabPanelShell
      title="Deal Options"
      subtitle={`${roomCount} room ${roomCount === 1 ? 'type' : 'types'} · ${deals.length} deal options`}
      actions={
        <Button
          disabled={viewModel.groups.length === 0}
          size="small"
          startIcon={expandedGroups.size === allGroupIds.length ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
          sx={{ textTransform: 'none' }}
          variant="outlined"
          onClick={toggleAllGroups}
        >
          {expandedGroups.size === allGroupIds.length ? 'Collapse all' : 'Expand all'}
        </Button>
      }
      contentSx={{ p: 0 }}
    >
      <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
        <DealOptionsTable
          activeDetailTabs={activeDetailTabs}
          expandedGroups={expandedGroups}
          expandedRows={expandedRows}
          groups={viewModel.groups}
          resolveRoomInfo={getRoomInfo}
          onActiveDetailTabChange={handleActiveDetailTabChange}
          onOpenRoomInfo={openRoomInfo}
          onToggleGroup={toggleGroup}
          onToggleRow={toggleRow}
        />
      </TableContainer>
      <DealOptionRoomInfoDialog
        activeTab={roomInfoTab}
        egsRows={selectedRoomInfo?.egsRows ?? []}
        salesforceSurcharges={selectedRoomInfo?.surcharges ?? []}
        selectedRoomInfo={selectedRoomInfo}
        onActiveTabChange={setRoomInfoTab}
        onClose={() => setSelectedRoomInfo(null)}
      />
    </TabPanelShell>
  );
}
